// Offer Survey Page - Satisfaction survey with emoji ratings
import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Ship
} from "lucide-react";


const EMOJI_RATINGS = [
  { value: 1, emoji: "😞", label: "Very Unsatisfied" },
  { value: 2, emoji: "😕", label: "Unsatisfied" },
  { value: 3, emoji: "😐", label: "Neutral" },
  { value: 4, emoji: "🙂", label: "Satisfied" },
  { value: 5, emoji: "😄", label: "Very Satisfied" }
];

export default function OfferSurveyPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const [selectedRating, setSelectedRating] = useState(null);
  const [feedback, setFeedback] = useState("");

  // Check for rating in URL params (from email click)
  const ratingFromUrl = searchParams.get("rating");

  useEffect(() => {
    fetchSurveyData();
  }, [token]);

  useEffect(() => {
    if (ratingFromUrl) {
      const rating = parseInt(ratingFromUrl);
      if (rating >= 1 && rating <= 5) {
        setSelectedRating(rating);
      }
    }
  }, [ratingFromUrl]);

  const fetchSurveyData = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/survey-page/${token}`);
      setSurveyData(response.data);
      
      if (response.data.already_completed) {
        setSubmitted(true);
      }
    } catch (err) {
      setError("This survey link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRating) {
      toast.error("Please select a rating");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/public/offer-survey/${token}?rating=${selectedRating}`, null, {
        params: { feedback: feedback || undefined }
      });
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (err) {
      toast.error("Failed to submit survey. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Expired</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center"
        >
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-4">Your feedback has been recorded. We truly appreciate your time.</p>
          <p className="text-sm text-gray-500">
            We're always working to improve our services for you.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 px-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8 text-center">
        <div className="inline-flex items-center gap-2 bg-[#1e3a5f] text-white px-6 py-3 rounded-full mb-6">
          <Ship className="h-5 w-5" />
          <span className="font-semibold">Europacific</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">How Was Your Experience?</h1>
        <p className="text-gray-600">
          Quick survey for quote <strong>{surveyData?.offer_number}</strong>
        </p>
      </div>

      {/* Survey Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center">
            How would you rate your overall experience?
          </h2>

          {/* Emoji Rating */}
          <div className="flex justify-center gap-4 mb-8">
            {EMOJI_RATINGS.map((rating) => {
              const isSelected = selectedRating === rating.value;
              
              return (
                <motion.button
                  key={rating.value}
                  onClick={() => setSelectedRating(rating.value)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                    isSelected
                      ? "bg-primary/10 ring-2 ring-primary"
                      : "hover:bg-gray-50"
                  }`}
                  data-testid={`rating-${rating.value}`}
                >
                  <span className={`text-5xl transition-transform ${isSelected ? "scale-125" : ""}`}>
                    {rating.emoji}
                  </span>
                  <span className={`text-xs font-medium ${
                    isSelected ? "text-primary" : "text-gray-500"
                  }`}>
                    {rating.label}
                  </span>
                </motion.button>
              );
            })}
          </div>

          {/* Selected rating confirmation */}
          {selectedRating && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-6"
            >
              <p className="text-primary font-medium">
                You selected: {EMOJI_RATINGS.find(r => r.value === selectedRating)?.label}
              </p>
            </motion.div>
          )}

          {/* Feedback text */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Any additional comments? (optional)
            </label>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell us what we could do better, or what you loved about our service..."
              className="min-h-[100px]"
              data-testid="survey-feedback-text"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedRating}
            className="w-full h-12 bg-primary hover:bg-primary/90"
            data-testid="submit-survey-btn"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Footer */}
      <div className="max-w-2xl mx-auto mt-8 text-center text-gray-500 text-sm">
        <p>Your feedback helps us continuously improve our services.</p>
        <p className="mt-2">© 2026 Europacific d.o.o. | Logistics Simplified</p>
      </div>
    </div>
  );
}
